import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import usePrevious from 'components/hooks/usePrevious';
import { useDispatch } from 'react-redux';
import Select, { components } from 'react-select';
import SidepanelFooter from 'screens/admin/common/sidepanelFooter';
import styled from 'styled-components';
import { Divider1, StyledTypography, VerticallyAlignedFlexContainer } from 'styles/common';

import { TRACKING_OPTIONS } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { formatPluginFiltersToObject, formatPluginFiltersToArray, findAndReplaceString } from 'constants/helper';
import { KH_LINKS } from 'constants/knowledgeHubLinks';

import { getMetrics, getQueryString, linkMetric } from 'services/goalTracking';
import { showSuccessSnackbar, showErrorSnackbar } from 'services/snackbar';

import TooltipInfo from 'uiToolkit/TooltipInfo';

import CustomRatio from './CustomRatio';
import PluginFilters from './PluginFilters';
import QueryStringSection from './QueryStringSection';
import PluginImage from './pluginImage';
import { StyledTextField, MultilineTextField } from './styles';

const TRACKING_QUERY_TYPES = {
  CUSTOM: 'CUSTOM',
  CUSTOM_RATIO: 'CUSTOM_RATIO',
  CUSTOM_PERCENTAGE: 'CUSTOM_PERCENTAGE',
};

const TrackingDetails = ({ toggleShowTrackingDetails, trackingOption, goalDetails, getTaskData }) => {
  const [loading, setLoading] = useState(false);
  const [pluginMetric, setPluginMetric] = useState(null);
  const [allowedFilters, setAllowedFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const prevPluginMetric = usePrevious(pluginMetric);

  const [queryString, setQueryString] = useState('');
  const dispatch = useDispatch();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  let info = null;
  if (trackingOption?.value === TRACKING_OPTIONS[0].value) {
    const updatedJiraDescriptionWithoutGoals = findAndReplaceString(
      KH_LINKS.JIRA.description,
      modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE),
      'goals'
    );
    const finalUpdatedJiraDescription = findAndReplaceString(
      updatedJiraDescriptionWithoutGoals,
      modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE),
      'objectives'
    );
    info = { title: finalUpdatedJiraDescription, url: KH_LINKS.JIRA.url };
  }
  if (trackingOption?.value === TRACKING_OPTIONS[1].value) {
    const updatedSalesforceDescriptionWithoutGoals = findAndReplaceString(
      KH_LINKS.SALESFORCE.description,
      modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE),
      'goals'
    );
    const finalSalesforceDescription = findAndReplaceString(
      updatedSalesforceDescriptionWithoutGoals,
      modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE),
      'objectives'
    );
    info = { title: finalSalesforceDescription, url: KH_LINKS.SALESFORCE.url };
  }

  useEffect(() => {
    if (trackingOption && trackingOption.value) {
      setLoading(true);
      getMetrics({ taskId: goalDetails.id, pluginType: trackingOption.value })
        .then((res) => {
          const metrics = res.metrics.map((m) => ({
            ...m,
            label: m.name,
            value: m.id,
            subLabel: m.isPredefined ? 'Predefined' : 'Custom',
          }));
          if (trackingOption.value !== 'DARWINBOX') {
            metrics.push({
              value: TRACKING_QUERY_TYPES.CUSTOM,
              label: 'Custom Metric',
              subLabel: 'Create a custom metric',
            });
            metrics.push({
              value: TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE,
              label: 'Custom Percentage Metric',
              subLabel: 'Create a custom % metric',
            });
          }
          setPluginMetric({
            ...res,
            metrics,
            selectedMetricId: res.selectedMetricId
              ? res.selectedMetricId
              : res.isCustomQuery
                ? TRACKING_QUERY_TYPES.CUSTOM
                : null,
            isCustomQuery: res.selectedMetricId ? false : res.isCustomQuery,
            query: res.query,
          });

          if (res.queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) {
            setQueryString(`${res.query}/${res.ratioQuery}`);
          }

          if (res.selectedMetricId) {
            const selected = res.metrics.filter((m) => m.id === res.selectedMetricId)[0];
            if (selected.filterGroup) {
              const filterGroup = res.filterGroups.filter((fg) => fg.name === selected.filterGroup)[0];
              setAllowedFilters(filterGroup.filters);
              setSelectedFilters(formatPluginFiltersToObject(selected.filters, filterGroup.filters));
            }
          }
        })
        .catch((err) => dispatch(showErrorSnackbar(err.message)))
        .finally(() => setLoading(false));
    }
  }, [trackingOption]);

  useEffect(() => {
    if (
      !pluginMetric ||
      !pluginMetric.selectedMetricId ||
      pluginMetric.isCustomQuery ||
      trackingOption.value === 'DARWINBOX' ||
      (prevPluginMetric && prevPluginMetric.name !== pluginMetric.name)
    ) {
      return;
    }
    setLoading(true);
    setQueryString('');
    const isFiltersApplied = Object.keys(selectedFilters).filter((f) => selectedFilters[f].length).length > 0;
    if (isFiltersApplied) {
      getQueryString({
        filters: formatPluginFiltersToArray(selectedFilters, allowedFilters),
        metricId: pluginMetric.selectedMetricId,
      })
        .then((res) => setQueryString(res))
        .finally(() => setLoading(false));
    } else {
      const selected = pluginMetric.metrics.filter((m) => m.value === pluginMetric.selectedMetricId)[0];

      if (selected.queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) {
        const queryString = `${selected.query || ''}/${selected.ratioQuery || ''}`;
        setQueryString(queryString);
      } else {
        setQueryString(selected.query);
      }
      setLoading(false);
    }
  }, [pluginMetric, selectedFilters]);

  const handlePluginMetricChange = (key, value, retainFilters) => {
    const pluginMetric1 = { ...pluginMetric, [key]: value };
    if (key === 'selectedMetricId') {
      if (![TRACKING_QUERY_TYPES.CUSTOM, TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE].includes(value)) {
        const selected = pluginMetric.metrics.filter((m) => m.value === value)[0];

        pluginMetric1.query = '';
        pluginMetric1.isCustomQuery = false;
        pluginMetric1.queryType = selected.queryType;

        if (selected.queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) {
          pluginMetric1.ratioQuery = selected.ratioQuery;
        }

        if (selected.filterGroup) {
          const filterGroup = pluginMetric.filterGroups.filter((fg) => fg.name === selected.filterGroup)[0];
          setAllowedFilters(filterGroup.filters);
          if (!retainFilters) {
            setSelectedFilters(formatPluginFiltersToObject(selected.filters, filterGroup.filters));
          }
        } else {
          pluginMetric1.query = selected.query;
          setAllowedFilters([]);
          setSelectedFilters([]);
        }
      } else {
        pluginMetric1.isCustomQuery = true;
        if (value === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) {
          pluginMetric1.queryType = TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE;
        } else {
          pluginMetric1.queryType = TRACKING_QUERY_TYPES.CUSTOM;
        }
        pluginMetric1.query = '';
        pluginMetric1.ratioQuery = '';
        setQueryString('');
      }
    }
    setPluginMetric(pluginMetric1);
  };

  const handleCustomRatioChange = (key, value) => {
    const data = { ...pluginMetric, [key]: value };
    let qs = '';
    if (key === 'query') {
      qs = `${value}/${pluginMetric.ratioQuery || ''}`;
    }
    if (key === 'ratioQuery') {
      qs = `${pluginMetric.query || ''}/${value}`;
    }
    setPluginMetric(data);
    setQueryString(qs);
  };

  const handleSaveClick = () => {
    const taskId = goalDetails.id;
    const pluginType = trackingOption.value;
    const { isCustomQuery } = pluginMetric;
    const metricId = isCustomQuery ? null : pluginMetric.selectedMetricId;
    const filtersApplied = formatPluginFiltersToArray(selectedFilters, allowedFilters);
    const filters = isCustomQuery || filtersApplied.length === 0 ? null : filtersApplied;
    const name = isPredefined() && filtersApplied.length === 0 ? null : pluginMetric.name;
    const query = isCustomQuery ? pluginMetric.query : null;
    const ratioQuery =
      pluginMetric.selectedMetricId === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE ? pluginMetric.ratioQuery : null;
    const queryType = [TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE, TRACKING_QUERY_TYPES.CUSTOM].includes(
      pluginMetric.selectedMetricId
    )
      ? pluginMetric.selectedMetricId
      : null;
    setLoading(true);
    linkMetric({
      taskId,
      pluginType,
      metricId,
      filters,
      name,
      isCustomQuery,
      query,
      ratioQuery,
      queryType,
    })
      .then((res) => {
        dispatch(showSuccessSnackbar(res.message || 'Saved successfully!'));
        getTaskData();
        toggleShowTrackingDetails(false);
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => setLoading(false));
  };

  const handleCancelClick = () => {
    toggleShowTrackingDetails(false);
  };

  const handleFilterApply = (selected, key) => {
    setSelectedFilters({
      ...selectedFilters,
      [key]: selected,
    });
  };

  const isNonPredefinedAndNonCustom = () => {
    const selected = pluginMetric.metrics.filter((m) => m.value === pluginMetric.selectedMetricId)[0];
    const res = Boolean(selected && !selected.isPredefined && !selected.isCustomQuery);
    return res;
  };

  const isNewCustom = () => {
    if (pluginMetric.queryType === TRACKING_QUERY_TYPES.CUSTOM) {
      return (
        pluginMetric.selectedMetricId === TRACKING_QUERY_TYPES.CUSTOM &&
        pluginMetric.queryType === TRACKING_QUERY_TYPES.CUSTOM
      );
    }
    return false;
  };

  const isNewCustomPercentage = () => {
    if (pluginMetric.queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) {
      return (
        pluginMetric.selectedMetricId === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE &&
        pluginMetric.queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE
      );
    }
    return false;
  };

  const isPredefined = () => {
    const selected = pluginMetric.metrics.filter((m) => m.value === pluginMetric.selectedMetricId)[0];
    return Boolean(selected && selected.isPredefined && !selected.isCustomQuery);
  };

  const isNameVisible = () => {
    const filtersApplied = formatPluginFiltersToArray(selectedFilters, allowedFilters);
    return (
      (isPredefined() && filtersApplied.length !== 0) ||
      // isNonPredefinedAndNonCustom() === true ||
      isNewCustom() ||
      isNewCustomPercentage()
    );
  };

  const editMetric = () => {
    const selected = pluginMetric.metrics.filter((m) => m.value === pluginMetric.selectedMetricId)[0];
    handlePluginMetricChange('selectedMetricId', selected.baseMetricId, true);
  };

  const removeAllFilters = () => {
    setSelectedFilters([]);
  };

  const Option = (props) => (
    <components.Option {...props}>
      <div style={{ position: 'relative' }}>
        <div>{props.data.label}</div>
        <SubLabelText variant="body" color={props.isSelected ? '#F7F7F7' : '#999'}>
          {props.data.subLabel}
        </SubLabelText>
      </div>
    </components.Option>
  );

  return trackingOption ? (
    <div>
      <StyledGrid container>
        <Grid item>
          <PluginImage type={trackingOption.value} />
        </Grid>
        <HeaderText item>
          <VerticallyAlignedFlexContainer>
            <StyledTypography variant="h6" color="#333">
              Track via {trackingOption.label}
            </StyledTypography>
            {info && <TooltipInfo title={info.title} linkUrl={info.url} leftGutter={1} />}
          </VerticallyAlignedFlexContainer>
          <StyledTypography variant="body" color="#666">
            Automatically track metric with predefined or custom queries
          </StyledTypography>
        </HeaderText>
      </StyledGrid>
      {pluginMetric && (
        <Container>
          {isNameVisible() && (
            <>
              <StyledTypography variant="body2" color="#999">
                Name*
              </StyledTypography>
              <StyledTextField
                value={pluginMetric.name}
                placeholder="Enter tracking name for future use"
                onChange={(e) => handlePluginMetricChange('name', e.target.value)}
                variant="outlined"
              />
            </>
          )}
          <StyledTypography variant="body2" color="#999">
            Please select how do you want to track progress
          </StyledTypography>

          <StyledSelect
            value={pluginMetric.metrics.filter((m) => m.value === pluginMetric.selectedMetricId)[0]}
            placeholder="Track progress by"
            options={pluginMetric.metrics}
            onChange={(e) => handlePluginMetricChange('selectedMetricId', e.value)}
            components={{ Option }}
            // menuIsOpen={true}
          />
          {(pluginMetric.selectedMetricId === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE ||
            pluginMetric.queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) && (
            <CustomRatio
              disabled={pluginMetric.selectedMetricId !== TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE}
              pluginMetric={pluginMetric}
              handleChange={handleCustomRatioChange}
            />
          )}
          {pluginMetric.selectedMetricId && (
            <>
              {!pluginMetric.isCustomQuery ||
              pluginMetric.selectedMetricId === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE ? (
                <>
                  {!pluginMetric.isCustomQuery && allowedFilters.length > 0 && (
                    <PluginFilters
                      disabled={isNonPredefinedAndNonCustom()}
                      editMetric={editMetric}
                      filters={allowedFilters}
                      selectedFilters={selectedFilters}
                      updateFilters={handleFilterApply}
                      removeAllFilters={removeAllFilters}
                    />
                  )}
                  {trackingOption.value !== 'DARWINBOX' && (
                    <>
                      <Divider1 topGutter={2} bottomGutter={1} />
                      <QueryStringSection query={queryString} selectedFilters={selectedFilters} loading={loading} />
                    </>
                  )}
                </>
              ) : (
                <>
                  <br />
                  <StyledTypography variant="body2" color="#999">
                    Enter your custom query here
                  </StyledTypography>
                  <MultilineTextField
                    multiline
                    value={pluginMetric.query}
                    onChange={(e) => handlePluginMetricChange('query', e.target.value)}
                    variant="outlined"
                    rows={4}
                  />
                </>
              )}
            </>
          )}
        </Container>
      )}

      <SidepanelFooter
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
        disabled={
          loading ||
          !pluginMetric ||
          !pluginMetric.selectedMetricId ||
          (pluginMetric.isCustomQuery && pluginMetric.query === '') ||
          (isNameVisible() && !pluginMetric.name)
        }
      />
    </div>
  ) : (
    <div />
  );
};

const StyledGrid = styled(Grid)`
  border-bottom: 1px solid #eee;
  position: relative;
  padding: 0.35rem 0 1rem 0;
`;

const HeaderText = styled(Grid)`
  position: absolute;
  top: 39%;
  transform: translateY(-50%);
  left: 5rem;
`;

const Container = styled.div`
  padding: 1.4rem 4px 0 4px;
  height: 65vh;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    width: 0;
    background: transparent; /* Optional: just make scrollbar invisible */
  }
  &::-webkit-scrollbar-thumb {
    background: #ff0000;
  }
`;

const StyledSelect = styled(Select)`
  margin-top: 0.5rem;
  font-size: 0.8rem;
`;

const SubLabelText = styled(StyledTypography)`
  position: absolute;
  right: 8px;
  top: 0;
  max-width: 60%;
  text-align: right;
`;

export default TrackingDetails;
